'use client';

import { ReactNode, useEffect, useRef } from 'react';
import { Info } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { FrontendUpdateNotice } from '@/components/system/FrontendUpdateNotice';
import { PwaRegister } from '@/components/PwaRegister';
import { ConfirmProvider } from '@/components/ui/ConfirmDialog';
import { UpgradeModal } from '@/components/plans/UpgradeModal';
import { CustomPlanRequestModal } from '@/components/plans/CustomPlanRequestModal';

interface ProvidersProps {
  children: ReactNode;
}

export function Providers({ children }: ProvidersProps) {
  const lastOfflineToastAtRef = useRef(0);
  const lastNotFoundToastAtRef = useRef(0);

  useEffect(() => {
    const OFFLINE_TOAST_COOLDOWN_MS = 8000;
    const NOT_FOUND_TOAST_COOLDOWN_MS = 6000;

    const handleOffline = (event: Event) => {
      const now = Date.now();
      if (now - lastOfflineToastAtRef.current < OFFLINE_TOAST_COOLDOWN_MS) return;
      lastOfflineToastAtRef.current = now;

      const detail = (event as CustomEvent<{ message?: string }>).detail;
      toast.error(detail?.message || 'Unable to reach the server. Check your connection and try again.');
    };

    const handleNotFound = (event: Event) => {
      const detail = (event as CustomEvent<{ url?: string; message?: string }>).detail;
      const url = detail?.url || '';

      // Ignore background or polling endpoints that may fail softly
      if (
        url.includes('/notifications') ||
        url.includes('/badges') ||
        url.includes('/unread') ||
        url.includes('/chats') ||
        url.includes('/health') ||
        url.includes('/search') ||
        url.includes('/hackathon/submission') ||
        url.includes('/qa')
      ) {
        return;
      }

      const now = Date.now();
      if (now - lastNotFoundToastAtRef.current < NOT_FOUND_TOAST_COOLDOWN_MS) return;
      lastNotFoundToastAtRef.current = now;

      const message = detail?.message && !detail.message.toLowerCase().includes('not found')
        ? detail.message
        : 'The requested resource could not be found.';

      toast(message, {
        icon: <Info className="h-5 w-5 shrink-0 text-amber-400" aria-hidden />,
        style: {
          background: '#1E293B',
          color: '#F8FAFC',
          border: '1px solid #334155',
        },
      });
      if (process.env.NODE_ENV === 'development' && detail?.url) {
        console.warn('API 404:', detail.url);
      }
    };

    window.addEventListener('api:offline', handleOffline);
    window.addEventListener('api:not-found', handleNotFound);

    return () => {
      window.removeEventListener('api:offline', handleOffline);
      window.removeEventListener('api:not-found', handleNotFound);
    };
  }, []);

  return (
    <ConfirmProvider>
      <PwaRegister />
      <FrontendUpdateNotice />
      {children}
      <UpgradeModal />
      <CustomPlanRequestModal />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1E293B',
            color: '#F8FAFC',
            border: '1px solid #334155',
          },
          success: {
            iconTheme: {
              primary: '#10B981',
              secondary: '#F8FAFC',
            },
          },
          error: {
            iconTheme: {
              primary: '#EF4444',
              secondary: '#F8FAFC',
            },
          },
        }}
      />
    </ConfirmProvider>
  );
}

